// 房贷计算工具类
export const mortgageCalculator = {
  // 计算等额本息
  calculateEqualPayment(amount, term, rate, prepaymentInfo = null, startYear = 2025, startMonth = 1) {
    const monthlyRate = rate / 100 / 12
    const monthlyPayment = amount * monthlyRate * Math.pow(1 + monthlyRate, term) / 
                          (Math.pow(1 + monthlyRate, term) - 1)
    const totalPayment = monthlyPayment * term
    const totalInterest = totalPayment - amount
    
    return {
      monthlyPayment,
      totalPayment,
      totalInterest,
      loanAmount: amount,
      paymentSchedule: this.generateEqualPaymentSchedule(amount, term, rate, monthlyPayment, prepaymentInfo, startYear, startMonth)
    }
  },
  
  // 计算等额本金
  calculateEqualPrincipal(amount, term, rate, prepaymentInfo = null, startYear = 2025, startMonth = 1) {
    const monthlyRate = rate / 100 / 12
    const monthlyPrincipal = amount / term
    const monthlyDecrease = monthlyPrincipal * monthlyRate  // 每月递减金额
    let remainingPrincipal = amount
    let totalInterest = 0
    const paymentSchedule = []
    
    for (let i = 1; i <= term; i++) {
      const interestPayment = remainingPrincipal * monthlyRate
      const monthlyPayment = monthlyPrincipal + interestPayment
      
      // 检查是否是提前还款月份
      if (prepaymentInfo && prepaymentInfo.month === i) {
        // 先添加正常还款行
        paymentSchedule.push({
          period: i,
          date: this.getPaymentDate(i, startYear, startMonth),
          monthlyPayment,
          principal: monthlyPrincipal,
          interest: interestPayment,
          remainingPrincipal: Math.max(0, remainingPrincipal - monthlyPrincipal),
          isPrepayment: true,
          prepaymentAmount: prepaymentInfo.amount,
          prepaymentType: 'monthly'
        })
        
        // 更新剩余本金（正常还款后）
        remainingPrincipal = Math.max(0, remainingPrincipal - monthlyPrincipal)
        totalInterest += interestPayment
        
        // 添加提前还款行
        paymentSchedule.push({
          period: i,
          date: this.getPaymentDate(i, startYear, startMonth),
          monthlyPayment: 0,
          principal: 0,
          interest: 0,
          remainingPrincipal: Math.max(0, remainingPrincipal - prepaymentInfo.amount),
          isPrepayment: true,
          prepaymentAmount: prepaymentInfo.amount,
          prepaymentType: 'prepayment'
        })
        
        // 更新剩余本金（提前还款后）
        remainingPrincipal = Math.max(0, remainingPrincipal - prepaymentInfo.amount)
        
        // 如果剩余本金为0，提前结束
        if (remainingPrincipal <= 0) {
          break
        }
      } else {
        // 正常还款
        paymentSchedule.push({
          period: i,
          date: this.getPaymentDate(i, startYear, startMonth),
          monthlyPayment,
          principal: monthlyPrincipal,
          interest: interestPayment,
          remainingPrincipal: Math.max(0, remainingPrincipal - monthlyPrincipal),
          isPrepayment: false
        })
        
        // 更新剩余本金
        remainingPrincipal = Math.max(0, remainingPrincipal - monthlyPrincipal)
        totalInterest += interestPayment
      }
    }
    
    return {
      monthlyPayment: paymentSchedule[0].monthlyPayment, // 首月还款额
      monthlyDecrease: monthlyDecrease, // 每月递减金额
      totalPayment: amount + totalInterest,
      totalInterest,
      loanAmount: amount,
      paymentSchedule
    }
  },
  
  // 生成等额本息还款计划
  generateEqualPaymentSchedule(amount, term, rate, monthlyPayment, prepaymentInfo = null, startYear = 2025, startMonth = 1) {
    const monthlyRate = rate / 100 / 12
    let remainingPrincipal = amount
    const schedule = []
    
    for (let i = 1; i <= term; i++) {
      const interestPayment = remainingPrincipal * monthlyRate
      const principalPayment = monthlyPayment - interestPayment
      
      // 检查是否是提前还款月份
      if (prepaymentInfo && prepaymentInfo.month === i) {
        // 先添加正常还款行
        schedule.push({
          period: i,
          date: this.getPaymentDate(i, startYear, startMonth),
          monthlyPayment,
          principal: principalPayment,
          interest: interestPayment,
          remainingPrincipal: Math.max(0, remainingPrincipal - principalPayment),
          isPrepayment: true,
          prepaymentAmount: prepaymentInfo.amount,
          prepaymentType: 'monthly'
        })
        
        // 更新剩余本金（正常还款后）
        remainingPrincipal = Math.max(0, remainingPrincipal - principalPayment)
        
        // 添加提前还款行
        schedule.push({
          period: i,
          date: this.getPaymentDate(i, startYear, startMonth),
          monthlyPayment: 0,
          principal: 0,
          interest: 0,
          remainingPrincipal: Math.max(0, remainingPrincipal - prepaymentInfo.amount),
          isPrepayment: true,
          prepaymentAmount: prepaymentInfo.amount,
          prepaymentType: 'prepayment'
        })
        
        // 更新剩余本金（提前还款后）
        remainingPrincipal = Math.max(0, remainingPrincipal - prepaymentInfo.amount)
        
        // 如果剩余本金为0，提前结束
        if (remainingPrincipal <= 0) {
          break
        }
      } else {
        // 正常还款
        schedule.push({
          period: i,
          date: this.getPaymentDate(i, startYear, startMonth),
          monthlyPayment,
          principal: principalPayment,
          interest: interestPayment,
          remainingPrincipal: Math.max(0, remainingPrincipal - principalPayment),
          isPrepayment: false
        })
        
        // 更新剩余本金
        remainingPrincipal = Math.max(0, remainingPrincipal - principalPayment)
      }
    }
    
    return schedule
  },
  
  // 计算提前还款后的新计划
  calculatePrepaymentPlan(originalPlan, prepaymentData, originalLoanAmount, originalTerm, prepaymentMonthNumber, firstPaymentYear, firstPaymentMonth) {
    const {
      prepaymentMethod,
      prepaymentAmount,
      newInterestRate,
      newLoanTerm,
      newMonthlyPayment,
      newRepaymentMethod,
      adjustmentType,
      prepaymentYear,
      prepaymentMonth
    } = prepaymentData
    
    if (prepaymentMethod === 'full_prepayment') {
      return {
        monthlyPayment: 0,
        totalPayment: 0,
        totalInterest: 0,
        paymentSchedule: [],
        loanAmount: 0
      }
    }
    
    // 部分提前还款 - 从原始计划中获取提前还款时的剩余本金
    const prepaymentSchedule = originalPlan.paymentSchedule
    const prepaymentPeriod = prepaymentSchedule.find(p => p.period === prepaymentMonthNumber)
    let actualRemainingAmount = prepaymentPeriod ? prepaymentPeriod.remainingPrincipal : originalLoanAmount
    
    // 减去提前还款金额
    actualRemainingAmount = Math.max(0, actualRemainingAmount - (prepaymentAmount || 0))
    
    if (actualRemainingAmount <= 0) {
      return {
        monthlyPayment: 0,
        totalPayment: 0,
        totalInterest: 0,
        paymentSchedule: [],
        loanAmount: 0
      }
    }
    
    // 根据调整方案类型确定新的期数和还款方式
    let actualNewTerm
    const methodForNewPlan = newRepaymentMethod
    const rateForNewPlan = newInterestRate
    
    if (adjustmentType === 'keep_term') {
      // 保持原合同的剩余期数，重新计算月供
      actualNewTerm = originalTerm - prepaymentMonthNumber
    } else if (adjustmentType === 'keep_payment') {
      // 以提前还款当月的实际月供为上限，反推新的最短期限
      actualNewTerm = this.calculateTermForPayment(
        actualRemainingAmount,
        prepaymentPeriod.monthlyPayment,
        rateForNewPlan,
        methodForNewPlan
      )
    } else if (adjustmentType === 'adjust_payment') {
      // 以用户指定的月供为上限，反推新的最短期限
      actualNewTerm = this.calculateTermForPayment(
        actualRemainingAmount,
        newMonthlyPayment,
        rateForNewPlan,
        methodForNewPlan
      )
    } else if (adjustmentType === 'adjust_term') {
      // 使用用户指定的期限重新计算月供
      actualNewTerm = Number(newLoanTerm)
    } else {
      throw new Error('不支持的调整还款方案')
    }

    if (!Number.isInteger(actualNewTerm) || actualNewTerm <= 0) {
      throw new Error('新的贷款期限必须是大于0的整数')
    }
    
    // 新合同从提前还款的下一个月开始还款
    const nextMonth = prepaymentMonth === 12 ? { year: prepaymentYear + 1, month: 1 } : { year: prepaymentYear, month: prepaymentMonth + 1 }
    
    const newPlan = methodForNewPlan === 'equal_payment' 
      ? this.calculateEqualPayment(actualRemainingAmount, actualNewTerm, rateForNewPlan, null, nextMonth.year, nextMonth.month)
      : this.calculateEqualPrincipal(actualRemainingAmount, actualNewTerm, rateForNewPlan, null, nextMonth.year, nextMonth.month)
    
    // 生成包含提前还款行的完整还款计划
    const newSchedule = []
    
    // 添加提前还款前的正常还款（从原始计划中获取）
    for (let i = 1; i <= prepaymentMonthNumber; i++) {
      const originalPayment = originalPlan.paymentSchedule[i - 1]
      if (originalPayment) {
        newSchedule.push({
          ...originalPayment,
          period: i
        })
      }
    }
    
    // 添加提前还款行
    const prepaymentRow = {
      period: prepaymentMonthNumber,
      date: `${prepaymentYear}-${String(prepaymentMonth).padStart(2, '0')}`,
      monthlyPayment: 0,
      principal: 0,
      interest: 0,
      remainingPrincipal: actualRemainingAmount,
      isPrepayment: true,
      prepaymentAmount: prepaymentAmount,
      prepaymentType: 'prepayment'
    }
    newSchedule.push(prepaymentRow)
    
    // 添加提前还款后的新还款计划，调整期数
    newPlan.paymentSchedule.forEach((payment, index) => {
      newSchedule.push({
        ...payment,
        period: prepaymentMonthNumber + 1 + index,
        date: this.getPaymentDate(index + 1, nextMonth.year, nextMonth.month)
      })
    })
    
    // 计算总利息（只计算提前还款后的利息）
    const totalInterest = newPlan.paymentSchedule.reduce((sum, payment) => sum + payment.interest, 0)
    const totalPayment = actualRemainingAmount + totalInterest
    
    // 确保loanAmount字段正确设置
    return {
      monthlyPayment: newPlan.monthlyPayment,
      monthlyDecrease: newPlan.monthlyDecrease,
      totalPayment: totalPayment,
      totalInterest: totalInterest,
      paymentSchedule: newSchedule,
      loanAmount: actualRemainingAmount,
      loanTerm: actualNewTerm,
      interestRate: rateForNewPlan,
      repaymentMethod: methodForNewPlan
    }
  },
  
  // 主计算方法
  calculate(formData) {
    const {
      loanAmount,
      loanTerm,
      interestRate,
      repaymentMethod,
      firstPaymentYear,
      firstPaymentMonth,
      prepaymentMethod,
      prepaymentAmount,
      prepaymentYear,
      prepaymentMonth,
      newInterestRate,
      newLoanTerm,
      newRepaymentMethod
    } = formData
    
    // 输入验证
    if (!loanAmount || !loanTerm || !interestRate || loanAmount <= 0 || loanTerm <= 0 || interestRate <= 0) {
      throw new Error('贷款金额、期限和利率必须大于0')
    }
    
    // 提前还款月份不再扣当月正常月供：月份差就是此前已还期数
    const prepaymentMonthNumber = prepaymentYear && prepaymentMonth ? 
      (prepaymentYear - firstPaymentYear) * 12 + (prepaymentMonth - firstPaymentMonth) : 0

    if (prepaymentMonthNumber < 1 || prepaymentMonthNumber >= loanTerm) {
      throw new Error('提前还款日期必须在原贷款期限内')
    }
    
    // 贷款时间是合同生效月，第一期正常还款发生在下个月
    const firstPaymentDate = this.getNextMonth(firstPaymentYear, firstPaymentMonth)

    // 准备提前还款信息
    let prepaymentInfo = null
    if (prepaymentMethod === 'partial_prepayment' && prepaymentAmount > 0) {
      prepaymentInfo = {
        month: prepaymentMonthNumber,
        amount: prepaymentAmount
      }
    } else if (prepaymentMethod === 'full_prepayment') {
      // 全额提前还款需要先计算到提前还款时的剩余本金
      const tempPlan = repaymentMethod === 'equal_payment' 
        ? this.calculateEqualPayment(loanAmount, loanTerm, interestRate, null, firstPaymentDate.year, firstPaymentDate.month)
        : this.calculateEqualPrincipal(loanAmount, loanTerm, interestRate, null, firstPaymentDate.year, firstPaymentDate.month)
      
      // 找到提前还款时的剩余本金
      const prepaymentSchedule = tempPlan.paymentSchedule
      const prepaymentPeriod = prepaymentSchedule.find(p => p.period === prepaymentMonthNumber)
      const remainingAmount = prepaymentPeriod ? prepaymentPeriod.remainingPrincipal : loanAmount
      
      prepaymentInfo = {
        month: prepaymentMonthNumber,
        amount: remainingAmount
      }
    }
    
    // 计算原始还款计划（不包含提前还款）
    const originalPlan = repaymentMethod === 'equal_payment' 
      ? this.calculateEqualPayment(loanAmount, loanTerm, interestRate, null, firstPaymentDate.year, firstPaymentDate.month)
      : this.calculateEqualPrincipal(loanAmount, loanTerm, interestRate, null, firstPaymentDate.year, firstPaymentDate.month)
    
    // 计算提前还款后的计划
    const prepaymentPlan = this.calculatePrepaymentPlan(originalPlan, {
      prepaymentMethod,
      prepaymentAmount: prepaymentAmount || 0,
      newInterestRate,
      newLoanTerm,
      newMonthlyPayment: formData.newMonthlyPayment,
      newRepaymentMethod,
      adjustmentType: formData.adjustmentType,
      prepaymentYear,
      prepaymentMonth
    }, loanAmount, loanTerm, prepaymentMonthNumber, firstPaymentYear, firstPaymentMonth)
    
    // 只比较提前还款日之后的利息；此前已经支付的利息不能计入节省额
    const paidInterest = originalPlan.paymentSchedule
      .slice(0, prepaymentMonthNumber)
      .reduce((sum, payment) => sum + payment.interest, 0)
    const remainingOriginalInterest = originalPlan.totalInterest - paidInterest
    const interestSaved = remainingOriginalInterest - prepaymentPlan.totalInterest
    // 不考虑违约金等费用时，实际少支付的总额就是少支付的利息
    const totalSaved = interestSaved
    const savingPercentage = originalPlan.totalPayment > 0 
      ? (totalSaved / originalPlan.totalPayment * 100).toFixed(2)
      : 0
    
    return {
      originalPlan: {
        ...originalPlan,
        loanAmount,
        loanTerm,
        interestRate,
        repaymentMethod
      },
      prepaymentPlan: {
        ...prepaymentPlan,
        loanAmount: prepaymentPlan.loanAmount || 0,
        loanTerm: prepaymentMethod === 'full_prepayment' ? 0 : prepaymentPlan.loanTerm,
        interestRate: prepaymentMethod === 'full_prepayment' ? 0 : newInterestRate,
        repaymentMethod: prepaymentMethod === 'full_prepayment' ? '' : newRepaymentMethod
      },
      comparison: {
        interestSaved,
        totalSaved,
        savingPercentage: parseFloat(savingPercentage)
      },
      formData
    }
  },
  
  // 获取还款日期
  getPaymentDate(period, startYear, startMonth) {
    const totalMonths = startMonth - 1 + period
    const year = startYear + Math.floor((totalMonths - 1) / 12)
    const month = ((totalMonths - 1) % 12) + 1
    return `${year}-${String(month).padStart(2, '0')}`
  },

  getNextMonth(year, month) {
    return month === 12
      ? { year: year + 1, month: 1 }
      : { year, month: month + 1 }
  },
  
  // 根据目标首月月供反推最短期限，与参考计算器的逐期匹配规则一致
  calculateTermForPayment(remainingAmount, targetPayment, interestRate, repaymentMethod) {
    const amount = Number(remainingAmount)
    const payment = Number(targetPayment)
    const monthlyRate = Number(interestRate) / 100 / 12

    if (!Number.isFinite(payment) || payment <= 0) {
      throw new Error('月供金额必须大于0')
    }

    for (let term = 1; term <= 600; term++) {
      let firstPayment

      if (repaymentMethod === 'equal_payment') {
        const factor = Math.pow(1 + monthlyRate, term)
        firstPayment = monthlyRate === 0
          ? amount / term
          : amount * monthlyRate * factor / (factor - 1)
      } else if (repaymentMethod === 'equal_principal') {
        firstPayment = amount / term + amount * monthlyRate
      } else {
        throw new Error('不支持的还款方式')
      }

      if (firstPayment <= payment) {
        return term
      }
    }

    throw new Error('月供金额过低，无法在600期内还清贷款')
  },
  
  // 格式化货币
  formatCurrency(amount) {
    if (amount === 0) return '0.00'
    return new Intl.NumberFormat('zh-CN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: false
    }).format(amount)
  }
}
